// Question 3: which assumptions did the evidence confirm or refute? Hard stops
// from step 3 first (no AI pass), then the AI's pending calls (Agree / Change
// call), then settled verdicts (Reopen), then assumptions settled by hand on
// the board, then what is still open. "Leave open" is a dismiss: open is never stored.

import { useState, type ReactNode } from 'react';
import { CheckCircle2, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge, type BadgeTone } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { pendingVerdicts, splitAssumptions, tallyVerdicts } from '@/lib/alignment-groups';
import { useApplyAlignment, useDismissAlignment, useReopenVerdict, useSetVerdict } from '@/hooks/use-alignment';
import { toast } from '@/hooks/use-toast';
import type {
  AlignmentAssumption,
  AlignmentProposalRow,
  AlignmentReport,
  VerdictCall,
  VerdictCitation,
  VerdictProposal,
  VerdictStatus,
} from '@/types/api';

const CALL_TONE: Record<VerdictCall, BadgeTone> = { confirmed: 'success', refuted: 'danger', open: 'neutral' };
const CALL_LABEL: Record<VerdictCall, string> = { confirmed: 'Confirmed', refuted: 'Refuted', open: 'Still open' };

export function VerdictsSection({
  projectId,
  report,
  row,
  canEdit,
}: {
  projectId: string;
  report: AlignmentReport;
  row: AlignmentProposalRow | null | undefined;
  canEdit: boolean;
}) {
  const apply = useApplyAlignment(projectId);
  const dismiss = useDismissAlignment(projectId);
  const reopen = useReopenVerdict(projectId);
  const setVerdict = useSetVerdict(projectId);
  // Reasoning folds by default: chip, title and evidence chips are usually
  // enough to decide. The header toggle broadcasts to every card; a card
  // clicked since follows its own click. Same pattern as the re-ground list.
  const [showReasons, setShowReasons] = useState(false);
  const [reasonSeq, setReasonSeq] = useState(0);

  const byId = new Map(report.assumptions.map((a) => [a.nodeId, a]));
  // An assumption deleted since the run is not actionable, so it is not shown.
  const pending = pendingVerdicts(row).filter((p) => byId.has(p.assumptionId));
  const pendingIds = new Set(pending.map((p) => p.assumptionId));
  const split = splitAssumptions(report.assumptions, pendingIds);
  const tally = tallyVerdicts(report.assumptions);
  const busy = apply.isPending || dismiss.isPending || reopen.isPending || setVerdict.isPending;

  const save = async (p: VerdictProposal, call: VerdictCall, note?: string) => {
    try {
      if (call === 'open') await dismiss.mutateAsync(p.id);
      else await apply.mutateAsync({ proposalId: p.id, call, ...(note ? { note } : {}) });
    } catch (err) {
      toast({ title: "Couldn't save that call", description: err instanceof Error ? err.message : '', variant: 'destructive' });
    }
  };
  const onReopen = async (a: AlignmentAssumption) => {
    try {
      await reopen.mutateAsync({ assumptionId: a.nodeId, decidedAt: a.verdict?.decidedAt });
    } catch (err) {
      toast({ title: "Couldn't reopen it", description: err instanceof Error ? err.message : '', variant: 'destructive' });
    }
  };
  // A call on an assumption from the Still open list. With the AI's earlier
  // read still in this run, apply it with the PM's call (keeps the summary and
  // citations); otherwise record the PM's own call with nothing cited.
  const saveOpen = async (a: AlignmentAssumption, aiRead: VerdictProposal | undefined, call: VerdictStatus, note?: string) => {
    try {
      if (aiRead) await apply.mutateAsync({ proposalId: aiRead.id, call, ...(note ? { note } : {}) });
      else await setVerdict.mutateAsync({ assumptionId: a.nodeId, call, ...(note ? { note } : {}) });
    } catch (err) {
      toast({ title: "Couldn't save that call", description: err instanceof Error ? err.message : '', variant: 'destructive' });
    }
  };

  // The AI's read a PM left open earlier in this run, per still-open assumption.
  const openReads = new Map(
    split.open.map((a) => [a.nodeId, row?.verdicts.find((v) => v.assumptionId === a.nodeId && v.status === 'dismissed')]),
  );
  const anyReasons =
    split.hardStops.some((a) => a.aiReasoning) ||
    pending.some((p) => p.summary) ||
    split.settled.some((a) => a.verdict?.summary) ||
    [...openReads.values()].some((r) => r?.summary);
  const everythingCalled = split.open.length === 0 && pending.length === 0 && split.settled.length > 0;

  return (
    <section className="rounded-lg border bg-card p-5 space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h2 className="font-semibold flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="text-muted-foreground">3.</span>
          Which assumptions did the evidence confirm or refute?
        </h2>
        {anyReasons && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-muted-foreground"
            aria-expanded={showReasons}
            onClick={() => {
              setShowReasons((v) => !v);
              setReasonSeq((n) => n + 1);
            }}
          >
            {showReasons ? 'Hide reasons' : 'Show reasons'}
          </Button>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        For each untested assumption, the AI looked through your market findings and what customers said. Confirmed
        means the evidence backs it, refuted means it goes against it, still open means nothing found settles it.
        Agree to record the call, or change it.
      </p>

      {report.assumptions.length === 0 ? (
        <p className="text-sm text-muted-foreground">No assumptions to weigh yet. Add them in step 3 first.</p>
      ) : (
        <>
          <p className="text-sm">
            {tally.confirmed} confirmed, {tally.refuted} refuted, {tally.open} still open.
          </p>
          {tally.refutedImportant > 0 && (
            <p className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm">
              {tally.refutedImportant === 1
                ? 'One of your most important assumptions was refuted. Decide whether the idea survives without it.'
                : `${tally.refutedImportant} of your most important assumptions were refuted. Decide whether the idea survives without them.`}
            </p>
          )}

          {split.hardStops.length > 0 && (
            <Block label="Hard stops" count={split.hardStops.length} line="The AI flagged these as impossible in step 3. They stand until you dismiss them there.">
              {split.hardStops.map((a) => (
                <HardStopRow key={a.nodeId} assumption={a} expandSignal={reasonSeq} expandTo={showReasons} />
              ))}
            </Block>
          )}

          {pending.length > 0 && (
            <Block label="Suggested" count={pending.length}>
              {pending.map((p) => (
                <ProposalRow key={p.id} proposal={p} assumption={byId.get(p.assumptionId)} canEdit={canEdit} busy={busy} onSave={save} expandSignal={reasonSeq} expandTo={showReasons} />
              ))}
            </Block>
          )}

          {split.open.length > 0 ? (
            <Block
              label="Still open"
              count={split.open.length}
              // Before the first run the AI has not looked; after it, the intro
              // already says what still open means and Set call speaks for itself.
              line={row ? undefined : 'The AI hasn\'t weighed these yet. Press “Weigh the evidence” above.'}
            >
              {split.open.map((a) => (
                <OpenRow
                  key={a.nodeId}
                  assumption={a}
                  // The AI's read the PM left open earlier, if this run has one:
                  // shown again when they come back to decide.
                  aiRead={openReads.get(a.nodeId)}
                  canEdit={canEdit}
                  busy={busy}
                  expandSignal={reasonSeq}
                  expandTo={showReasons}
                  onSave={(call, note, aiRead) => saveOpen(a, aiRead, call, note)}
                />
              ))}
            </Block>
          ) : everythingCalled ? (
            <p className="text-sm text-muted-foreground">Every untested assumption has a call.</p>
          ) : null}

          {split.settled.length > 0 && (
            <Block label="Settled" count={split.settled.length}>
              {split.settled.map((a) => (
                <SettledRow key={a.nodeId} assumption={a} canEdit={canEdit} busy={busy} onReopen={onReopen} expandSignal={reasonSeq} expandTo={showReasons} />
              ))}
            </Block>
          )}

          {split.known.length > 0 && (
            <Block label="Known, your call" count={split.known.length} line="Set on the assumption board, in step 3." defaultOpen={false}>
              {split.known.map((a) => (
                <li key={a.nodeId} className="rounded-md border p-3 text-sm space-y-1">
                  <p className="leading-snug">
                    <Badge tone="neutral" className="mr-2 align-middle">Known</Badge>
                    <span className="align-middle">{a.title || '(untitled)'}</span>
                  </p>
                  <Notes assumption={a} />
                </li>
              ))}
            </Block>
          )}


          <p className="text-xs text-muted-foreground">The AI reads the evidence. The decision is yours.</p>
        </>
      )}
    </section>
  );
}

function Block({
  label,
  count,
  line,
  defaultOpen = true,
  children,
}: {
  label: string;
  count: number;
  line?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  // One presentation for every group of assumptions: a label with its count
  // and the same fold, an optional one-line note, and rows that share one
  // anatomy (status chip, title, detail, one action). Known starts folded
  // because it is the PM's own call, not the signal.
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="space-y-2">
      <button
        type="button"
        className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-foreground"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
        {label} ({count})
      </button>
      {open && line && <p className="text-xs text-muted-foreground">{line}</p>}
      {open && <ul className="space-y-1.5">{children}</ul>}
    </div>
  );
}

// Board notes ride into step 7 the way dismiss notes always have.
function Notes({ assumption: a }: { assumption: AlignmentAssumption | undefined }) {
  if (!a) return null;
  return (
    <>
      {a.downgradeReason && (
        <p className="text-xs italic text-muted-foreground">You lowered this from critical: &ldquo;{a.downgradeReason}&rdquo;</p>
      )}
      {a.dismissNote && (
        <p className="text-xs italic text-muted-foreground">Impossible flag dismissed: &ldquo;{a.dismissNote}&rdquo;</p>
      )}
    </>
  );
}

// The AI's one-line argument for its call. Labelled, because unlabelled it
// reads as the assumption's own description; the label matches the Evidence
// and Your call lines so a card scans the same way every time.
function AiRead({ summary }: { summary: string }) {
  if (!summary) return null;
  return (
    <p className="text-sm">
      <span className="mr-1 text-muted-foreground">AI reasoning:</span>
      {summary}
    </p>
  );
}

// Per-card fold for the AI's reasoning. A card follows the header's last
// broadcast unless it was clicked since; keyed on the broadcast sequence so a
// new "show all" wins over an older per-card click (the re-ground list's pattern).
function useRowExpanded(expandSignal: number, expandTo: boolean): [boolean, () => void] {
  const [toggled, setToggled] = useState<{ seq: number; open: boolean } | null>(null);
  const expanded = toggled && toggled.seq === expandSignal ? toggled.open : expandTo;
  return [expanded, () => setToggled({ seq: expandSignal, open: !expanded })];
}

// The chevron before a card's chip. A card with nothing to fold keeps the
// space so chips line up down the list.
function ReasonChevron({ hasReason, expanded, onToggle }: { hasReason: boolean; expanded: boolean; onToggle: () => void }) {
  if (!hasReason) return <span className="mr-1 inline-block h-3.5 w-3.5 align-middle" aria-hidden="true" />;
  return (
    <button
      type="button"
      onClick={onToggle}
      className="mr-1 inline-flex align-middle text-muted-foreground"
      aria-expanded={expanded}
      aria-label={expanded ? 'Hide reasoning' : 'Show reasoning'}
    >
      {expanded ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
    </button>
  );
}

function HardStopRow({ assumption: a, expandSignal, expandTo }: { assumption: AlignmentAssumption; expandSignal: number; expandTo: boolean }) {
  const [expanded, toggleExpanded] = useRowExpanded(expandSignal, expandTo);
  return (
    <li className="rounded-md border p-3 text-sm space-y-1">
      <p className="leading-snug">
        <ReasonChevron hasReason={!!a.aiReasoning} expanded={expanded} onToggle={toggleExpanded} />
        <Badge tone="danger" className="mr-2 align-middle">Hard stop</Badge>
        <span className="align-middle">{a.title || '(untitled)'}</span>
      </p>
      {expanded && a.aiReasoning && <p className="text-sm text-foreground">{a.aiReasoning}</p>}
    </li>
  );
}

function SettledRow({
  assumption: a,
  canEdit,
  busy,
  onReopen,
  expandSignal,
  expandTo,
}: {
  assumption: AlignmentAssumption;
  canEdit: boolean;
  busy: boolean;
  onReopen: (a: AlignmentAssumption) => void;
  expandSignal: number;
  expandTo: boolean;
}) {
  const v = a.verdict!;
  const [expanded, toggleExpanded] = useRowExpanded(expandSignal, expandTo);
  return (
    <li className="rounded-md border p-3 text-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-1.5">
          <p className="leading-snug">
            <ReasonChevron hasReason={!!v.summary} expanded={expanded} onToggle={toggleExpanded} />
            <Badge tone={CALL_TONE[v.status]} className="mr-2 align-middle">{CALL_LABEL[v.status]}</Badge>
            <span className="align-middle">{a.title || '(untitled)'}</span>
          </p>
          {expanded && <AiRead summary={v.summary} />}
          <Citations citations={v.citations} />
          {v.source === 'pm' && (
            <p className="text-xs text-muted-foreground">
              Your call{v.note ? <span className="italic">: &ldquo;{v.note}&rdquo;</span> : null}
            </p>
          )}
        </div>
        {canEdit && (
          <Button size="sm" variant="ghost" className="h-7 shrink-0" disabled={busy} onClick={() => onReopen(a)}>
            Reopen
          </Button>
        )}
      </div>
    </li>
  );
}

function Citations({ citations }: { citations: VerdictCitation[] }) {
  const supports = citations.filter((c) => c.bearing === 'supports');
  const contradicts = citations.filter((c) => c.bearing === 'contradicts');
  if (citations.length === 0) return null;
  const line = (label: string, items: VerdictCitation[]) =>
    items.length > 0 && (
      <p className="text-xs leading-relaxed">
        <span className="mr-1 text-muted-foreground">{label}</span>
        {items.map((c) => (
          <Badge key={c.nodeId} tone={c.nodeType} variant="outline" className="mr-1 mb-1 align-middle">
            {c.title || '(untitled)'}
          </Badge>
        ))}
      </p>
    );
  return (
    <div>
      {line('Evidence for:', supports)}
      {line('Evidence against:', contradicts)}
    </div>
  );
}

// One pending call. Agree takes the AI's read (an open read is a dismiss);
// Change call opens the three options inline with an optional reason.
function ProposalRow({
  proposal: p,
  assumption,
  canEdit,
  busy,
  onSave,
  expandSignal,
  expandTo,
}: {
  proposal: VerdictProposal;
  assumption: AlignmentAssumption | undefined;
  canEdit: boolean;
  busy: boolean;
  onSave: (p: VerdictProposal, call: VerdictCall, note?: string) => void;
  expandSignal: number;
  expandTo: boolean;
}) {
  const [expanded, toggleExpanded] = useRowExpanded(expandSignal, expandTo);
  const [changing, setChanging] = useState(false);
  const [choice, setChoice] = useState<VerdictCall>(p.call);
  const [note, setNote] = useState('');
  const cancel = () => {
    setChanging(false);
    setChoice(p.call);
    setNote('');
  };

  // Changing a call is a mode: while it is open the row's actions are Save
  // and Cancel only, so Agree cannot be pressed by mistake with a reason typed.
  return (
    <li className="rounded-md border p-3 text-sm space-y-2">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-1.5">
          <p className="leading-snug">
            <ReasonChevron hasReason={!!p.summary} expanded={expanded} onToggle={toggleExpanded} />
            <Badge tone={CALL_TONE[p.call]} className="mr-2 align-middle">{CALL_LABEL[p.call]}</Badge>
            <span className="align-middle">{p.assumptionTitle || '(untitled)'}</span>
          </p>
          {expanded && <AiRead summary={p.summary} />}
          <Citations citations={p.citations} />
          <Notes assumption={assumption} />
        </div>
        {canEdit && (
          <div className="flex shrink-0 flex-col items-stretch gap-1">
            {changing ? (
              <>
                <Button size="sm" className="h-7" disabled={busy} onClick={() => onSave(p, choice, note.trim() || undefined)}>
                  Save
                </Button>
                <Button size="sm" variant="ghost" className="h-7" disabled={busy} onClick={cancel}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="outline" className="h-7" disabled={busy} onClick={() => onSave(p, p.call)}>
                  Agree
                </Button>
                <Button size="sm" variant="ghost" className="h-7" disabled={busy} aria-expanded={changing} onClick={() => setChanging(true)}>
                  Change call
                </Button>
              </>
            )}
          </div>
        )}
      </div>
      {changing && canEdit && (
        <CallEditor
          choice={choice}
          options={[
            ['confirmed', 'Confirmed'],
            ['refuted', 'Refuted'],
            ['open', 'Leave open'],
          ]}
          onChoice={setChoice}
          note={note}
          onNote={setNote}
        />
      )}
    </li>
  );
}

// The call picker plus the reason, shared by a pending proposal being changed
// and an open assumption being decided. Save and Cancel live in the row's
// action column, so the editor itself carries no buttons.
function CallEditor({
  choice,
  options,
  onChoice,
  note,
  onNote,
}: {
  choice: VerdictCall;
  options: Array<[VerdictCall, string]>;
  onChoice: (v: VerdictCall) => void;
  note: string;
  onNote: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="inline-flex overflow-hidden rounded-md border">
        {options.map(([v, label]) => (
          <button
            key={v}
            type="button"
            aria-pressed={choice === v}
            onClick={() => onChoice(v)}
            className={cn('px-2 py-1 text-xs', choice === v ? 'bg-accent font-medium' : 'text-muted-foreground hover:bg-accent/50')}
          >
            {label}
          </button>
        ))}
      </div>
      <textarea
        value={note}
        onChange={(e) => onNote(e.target.value)}
        placeholder="Why? (optional)"
        aria-label="Why? (optional)"
        maxLength={500}
        rows={2}
        className="w-full resize-y rounded-md border bg-transparent px-2 py-1 text-xs"
      />
    </div>
  );
}

// An assumption nothing has settled yet. It stays decidable: Set call opens
// the picker, and if the AI's read from this run was left open earlier it
// comes back with its evidence, so the PM decides with the same context.
function OpenRow({
  assumption: a,
  aiRead,
  canEdit,
  busy,
  onSave,
  expandSignal,
  expandTo,
}: {
  assumption: AlignmentAssumption;
  aiRead: VerdictProposal | undefined;
  canEdit: boolean;
  busy: boolean;
  onSave: (call: VerdictStatus, note: string | undefined, aiRead: VerdictProposal | undefined) => void;
  expandSignal: number;
  expandTo: boolean;
}) {
  const [expanded, toggleExpanded] = useRowExpanded(expandSignal, expandTo);
  const [editing, setEditing] = useState(false);
  const [choice, setChoice] = useState<VerdictStatus>('confirmed');
  const [note, setNote] = useState('');
  const cancel = () => {
    setEditing(false);
    setChoice('confirmed');
    setNote('');
  };

  return (
    <li className="rounded-md border p-3 text-sm space-y-2">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1 space-y-1">
          <p className="leading-snug">
            <ReasonChevron hasReason={!!aiRead?.summary} expanded={expanded} onToggle={toggleExpanded} />
            <Badge tone={CALL_TONE.open} className="mr-2 align-middle">{CALL_LABEL.open}</Badge>
            <span className="align-middle">{a.title || '(untitled)'}</span>
          </p>
          {aiRead && (
            <>
              {expanded && <AiRead summary={aiRead.summary} />}
              <Citations citations={aiRead.citations} />
            </>
          )}
          <Notes assumption={a} />
        </div>
        {canEdit && (
          <div className="flex shrink-0 flex-col items-stretch gap-1">
            {editing ? (
              <>
                <Button size="sm" className="h-7" disabled={busy} onClick={() => onSave(choice, note.trim() || undefined, aiRead)}>
                  Save
                </Button>
                <Button size="sm" variant="ghost" className="h-7" disabled={busy} onClick={cancel}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button size="sm" variant="ghost" className="h-7" disabled={busy} aria-expanded={editing} onClick={() => setEditing(true)}>
                Set call
              </Button>
            )}
          </div>
        )}
      </div>
      {editing && canEdit && (
        <div className="space-y-2">
          <CallEditor
            choice={choice}
            options={[
              ['confirmed', 'Confirmed'],
              ['refuted', 'Refuted'],
            ]}
            onChoice={(v) => setChoice(v === 'refuted' ? 'refuted' : 'confirmed')}
            note={note}
            onNote={setNote}
          />
        </div>
      )}
    </li>
  );
}
